/* eslint-disable react-hooks/exhaustive-deps */
import {
  createContext,
  useCallback,
  useReducer,
  useRef,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { io } from 'socket.io-client';
import { useSnackbar } from 'notistack';
import { Button } from '@mui/material';
import _ from 'lodash';
import queue from 'queue';
import { Buffer } from 'buffer';

import useAuth from 'src/hooks/useAuth';
import useSettings, { SettingsKeys } from 'src/hooks/useSettings';
import { apiConfig, env } from 'src/config';
import {
  DICTATION_PROVIDER,
  APP_BUILD,
  SETTINGS_DISCRETE_ENABLED_PATH,
  SETTINGS_PRIVATE_MODE_ALLOWED_PATH,
  RECOGNITION_ERROR_CODES,
  RECOGNITION_RELEASE_GRACE_MS,
  RECOGNITION_IDLE_DISCONNECT_MS,
  RECOGNITION_CONNECT_TIMEOUT_MS,
  RECOGNITION_READY_TIMEOUT_MS,
  RECOGNITION_RECONNECT_BASE_DELAY_MS,
  RECOGNITION_RECONNECT_MAX_DELAY_MS,
  RECOGNITION_MAX_RECONNECT_ATTEMPTS,
} from 'src/constants';
import usePrevious from 'src/hooks/usePrevious';
import { clientApi } from 'src/api/clientApi';
import useCasrInfo from 'src/hooks/useCasrInfo';
import useSubscription from 'src/hooks/useSubscription';
import logger from 'src/lib/logger';
import ProcessingHandler from 'src/lib/processing_handler';
import { getDeviceInfo } from 'src/utils/platform';
import { useReduxStorage } from 'src/redux/store';
import useAppInactivity from 'src/hooks/useAppInactivity';
import { createDeferred } from 'src/utils/deferred_promise';
import wait from 'src/utils/wait';

export const RECOGNITION_SAMPLE_TYPE = {
  int16: 'int16',
  int32: 'int32',
  float32: 'float32',
  float64: 'float64',
};

const initialState = {
  socket: null,
  isConnected: false,
  isConnecting: false,
  provider: null,
  modelInfo: null,
  modelLoadStatus: null,
  isReady: false,
  recognition: null,
  isRecognitionProcessing: false,
  error: null,
  initProgress: null,
};

const ACTION = {
  SET_CONNECTED: 'SET_CONNECTED',
  SET_PROVIDER: 'SET_PROVIDER',
  SET_MODEL_INFO: 'SET_MODEL_INFO',
  SET_MODEL_LOAD_STATUS: 'SET_MODEL_LOAD_STATUS',
  CONNECTION_READY: 'CONNECTION_READY',
  SET_RECOGNITION: 'SET_RECOGNITION',
  SET_RECOGNITION_PROCESSING: 'SET_RECOGNITION_PROCESSING',
  SET_ERROR: 'SET_ERROR',
  RESET_RECOGNITION: 'RESET_RECOGNITION',
};

const handlers = {
  [ACTION.SET_CONNECTED]: (state, action) => {
    const { isConnected } = action.payload;
    const result = {
      ...state,
      isConnected,
      provider: null,
      modelInfo: null,
      modelLoadStatus: null,
      isReady: false,
      recognition: null,
      isRecognitionProcessing: false,
    };
    if (isConnected) {
      result.error = null;
    }
    return result;
  },

  [ACTION.SET_PROVIDER]: (state, action) => {
    const { provider } = action.payload;
    return {
      ...state,
      provider,
      modelInfo: null,
      modelLoadStatus: null,
      isReady: false,
      recognition: null,
      isRecognitionProcessing: false,
      error: null,
    };
  },
  [ACTION.SET_MODEL_INFO]: (state, action) => ({
    ...state,
    modelInfo: action.payload.modelInfo,
  }),
  [ACTION.SET_MODEL_LOAD_STATUS]: (state, action) => ({
    ...state,
    modelLoadStatus: action.payload.status,
  }),
  [ACTION.CONNECTION_READY]: (state) => ({
    ...state,
    isReady: true,
  }),
  [ACTION.SET_PARTIAL_RECOGNITION]: (state, action) => {
    const recognitions = [];
    const fullText = _.get(state, 'partial_recognition.full_text');
    if (!_.isEmpty(fullText)) {
      recognitions.push(fullText);
    }
    if (!_.isEmpty(action.payload.text)) {
      recognitions.push(action.payload.text);
    }
    return {
      ...state,
      partial_recognition: {
        full_text: recognitions.join(' '),
        text: action.payload.text,
        unstable_text: action.payload.unstable_text,
      },
    };
  },
  [ACTION.SET_RECOGNITION]: (state, action) => ({
    ...state,
    partial_recognition: null,
    recognition: { date: new Date(), text: action.payload.text },
  }),
  [ACTION.SET_RECOGNITION_PROCESSING]: (state, action) => ({
    ...state,
    isRecognitionProcessing: action.payload.isProcessing,
  }),
  [ACTION.SET_ERROR]: (state, action) => ({
    ...state,
    error: action.payload.error,
  }),
  [ACTION.RESET_RECOGNITION]: (state) => ({
    ...state,
    partial_recognition: null,
    recognition: null,
  }),
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const useServerRecognition = () => {
  const { getToken, getRefreshToken, user, tokenIssuedAt } = useAuth();

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (socketRef.current?.connected && tokenIssuedAt) {
      refreshToken(getToken());
    }
  }, [tokenIssuedAt]);

  const socketRef = useRef(null);
  const processingHandlerRef = useRef(null);
  const isStreamProcessingRef = useRef(null);
  const readyTimerRef = useRef(null);
  const queueRef = useRef(queue({ concurrency: 1, autostart: true }));

  const settings = useSettings();
  const noAudioSaveRef = useRef({ isAllowed: false, until: null });
  // What this socket was last told. A fresh socket knows nothing, so it starts
  // undefined and the value is asserted on open.
  const lastSaveAudioRef = useRef(undefined);
  // Set only while a `save_audio: true` sent via the immediate-assert path
  // (see assertSaveAudio's `deferTrue`) is queued but has not actually gone
  // out yet. Lets a later change cancel that queued send instead of it
  // firing after something newer has already overtaken it.
  const pendingSaveAudioTrueRef = useRef(null);

  // The server stores this option per user and does nothing on a timer, so
  // the client owns expiry completely: a value that changes must reach the
  // server at the moment it changes, not on the next socket open or the next
  // recording. This effect covers both ways the effective value changes --
  // the user toggling the mode (its own dependency fires) and the window's
  // hour running out while the app sits idle (nothing else re-renders for
  // that, so a one-shot timer is scheduled for the exact moment). Neither
  // path opens a connection: assertSaveAudio no-ops when no socket is live,
  // and every other call site (socket open, startStreamProcessing,
  // stopStreamProcessing) still asserts too, so correctness never depends on
  // this timer actually firing -- a throttled background tab just means the
  // value lands a little later, at the next of those points instead.
  useEffect(() => {
    noAudioSaveRef.current = {
      isAllowed: !!_.get(user?.settings, SETTINGS_PRIVATE_MODE_ALLOWED_PATH),
      until: settings.state[SettingsKeys.privateModeUntil],
    };
    assertSaveAudio(true);

    const until = settings.state[SettingsKeys.privateModeUntil];
    if (!until) {
      return undefined;
    }

    let expiryTimerId = null;
    const armExpiryTimer = () => {
      const delay = until - Date.now();
      if (delay <= 0) {
        return;
      }
      expiryTimerId = setTimeout(() => {
        assertSaveAudio(true);
        // The host can fire a setTimeout a hair early. When it does, `until`
        // has not actually passed yet: the assert above computed the same
        // value as before and no-opped at the dedupe check, and nothing else
        // would ever retry. Re-check and rearm instead of relying solely on
        // the (looser) backstops elsewhere.
        if (Date.now() < until) {
          armExpiryTimer();
        }
      }, delay);
    };
    armExpiryTimer();

    return () => clearTimeout(expiryTimerId);
  }, [user, settings.state[SettingsKeys.privateModeUntil]]);

  const clearReadyTimer = () => {
    clearTimeout(readyTimerRef.current);
    readyTimerRef.current = null;
  };

  // A socket that opens but never reports `connection_ready` can never stream,
  // yet it still holds a slot on the recognition server. Drop it instead of
  // leaving it hanging. Deliberately no auto-retry here: if this ever fires
  // systematically it should be visible as an error, not as a reconnect loop.
  const startReadyTimer = () => {
    clearReadyTimer();
    readyTimerRef.current = setTimeout(() => {
      readyTimerRef.current = null;
      if (!socketRef.current?.connected) {
        return;
      }
      logger.error(
        '[ServerRecognitionContext] connection_ready timeout, dropping socket',
      );
      disconnect(true);
    }, RECOGNITION_READY_TIMEOUT_MS);
  };

  const onProcessingHandlerStateChange = (e) => {
    dispatch({
      type: ACTION.SET_RECOGNITION_PROCESSING,
      payload: { isProcessing: e.detail },
    });
  };

  const connect = async () => {
    const token = getToken();
    const refreshToken = getRefreshToken();
    if (!token || !refreshToken) {
      return false;
    }

    if (socketRef.current && socketRef.current.connected) {
      return true;
    }

    const onRecognition = (data) => {
      const text = data.text;
      // Logs that a result arrived, never the result itself: the recognized
      // text is the user's speech, and it has no business sitting in a console
      // that screen-sharing, a support session or a bug report can expose. The
      // timestamp is what this line is for -- recognition latency.
      console.log('recognition', new Date());
      dispatch({ type: ACTION.SET_RECOGNITION, payload: { text } });
      processingHandlerRef.current?.audioProcessed(data.request_id);
    };

    // The recognition test seam. On a dev build it is always on; on any other
    // build it turns on only when the page is opened with `?e2e` in the URL, so
    // the automated /dictate suite can drive recognition against nonprod too.
    // Safe to ship: `recognitionText` only feeds text into the user's OWN
    // dictation field (the same thing speaking would do) and `streamAudio` reads
    // a local asset — no data leaves the page, nothing is elevated, and it is
    // opt-in per navigation, so a normal visitor never triggers it.
    const e2eSeamEnabled =
      env.isDev ||
      (typeof window !== 'undefined' &&
        new URLSearchParams(window.location.search).has('e2e'));

    if (e2eSeamEnabled) {
      window.recognition = onRecognition;
      window.recognitionText = (text) => onRecognition({ text });

      window.streamAudio = async (path = '/test_audio.wav', type = 'int16') => {
        const response = await fetch(path);
        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();

        const arrayTypes = {
          int16: Int16Array,
          float32: Float32Array,
        };

        const arrayType = arrayTypes[type];

        const typedArray = new arrayType(arrayBuffer);
        const chunks = _.chunk(typedArray, 3200);
        for (const chunk of chunks) {
          const audioData = new arrayType(chunk);
          socketRef.current.emit('stream_audio_samples', audioData, type);
        }
        const audioData = new arrayType(new Array(16000));
        socketRef.current.emit('stream_audio_samples', audioData, type);

        //works with '/test_audio_2.wav' int16
        // const audioData = new Int16Array(arrayBuffer)
        // socketRef.current.emit('recognize_audio_samples', audioData, RECOGNITION_SAMPLE_TYPE.int16)
      };

      window.streamCompressedAudio = async (path = '/test_audio_3.wav') => {
        const response = await fetch(path);
        const blob = await response.blob();

        const splitBlob = (
          file,
          chunkSize /* chunkSize should be byte 1024*1 = 1KB */,
        ) => {
          let startPointer = 0;
          let endPointer = file.size;
          const cSize = Math.min(file.size, chunkSize);
          const chunks = [];
          while (startPointer < endPointer) {
            let newStartPointer = startPointer + cSize;
            chunks.push(file.slice(startPointer, newStartPointer));
            startPointer = newStartPointer;
          }
          return chunks;
        };

        const blobChunks = splitBlob(blob, 1024);

        for (const chunk of blobChunks) {
          socketRef.current.emit('stream_compressed_audio', chunk, blob.type);
        }
        socketRef.current.emit(
          'stream_compressed_audio',
          new Blob([Buffer.from(new Array(160000))]),
          { type: blob.type },
        );
      };
    }

    processingHandlerRef.current = new ProcessingHandler();
    processingHandlerRef.current.addEventListener(
      'change',
      onProcessingHandlerStateChange,
    );

    let socketLocal = socketRef.current;
    if (!socketLocal) {
      console.log('[Connect] Create');

      const options = {
        path: '/socket.io',
        autoConnect: true,
        withCredentials: true,
        transports: ['websocket', 'polling'],
        auth: (cb) => {
          cb({
            token: getToken(),
            refresh_token: getRefreshToken(),
            user_id: user.id,
          });
        },
      };
      try {
        const isDictationUser =
          user?.providers_enabled.includes(DICTATION_PROVIDER);
        const url = isDictationUser
          ? apiConfig.casrDictationSocketApi
          : apiConfig.casrSocketApi;
        const socket = io(url, options);
        socketLocal = socket;
        socketLocal.deferredPromise = createDeferred();
        socketRef.current = socketLocal;
      } catch (error) {
        console.log(
          `[Connect] io not a function but ${io}`,
          socketLocal,
          error,
        );
        return false;
      }

      socketLocal.io.on('open', () => {
        console.log('Socket connected', new Date());
        // First message on the socket, so it lands before any audio: the
        // recognition server applies options in the order they arrive and
        // audio is queued behind the handshake. A fresh socket knows
        // nothing, so `lastSaveAudioRef` is reset first to force the assert
        // through even if the value happens to match what an earlier socket
        // was last told.
        lastSaveAudioRef.current = undefined;
        assertSaveAudio();
        startReadyTimer();
        dispatch({
          type: ACTION.SET_CONNECTED,
          payload: { isConnected: true },
        });
        socketLocal.deferredPromise.resolve();
      });
      socketLocal.io.on('close', () => {
        console.log('Socket closed', new Date());
        clearReadyTimer();
        dispatch({
          type: ACTION.SET_CONNECTED,
          payload: { isConnected: false },
        });
      });

      const logErrorDebounced = _.debounce(logger.error.bind(logger), 10000, {
        leading: true,
        trailing: true,
      });
      socketLocal.io.on('error', (data) => {
        const errorMsg = data?.type || JSON.stringify(data);
        console.error('Socket error data', errorMsg);
        if (data?.type !== 'TransportError') {
          logErrorDebounced(
            `[ServerRecognitionContext] Socket error: ${errorMsg}`,
          );
        }

        dispatch({ type: ACTION.SET_ERROR, payload: { error: data } });

        processingHandlerRef.current?.error(data.request_id, data);
      });
      socketLocal.io.on('reset_alb_cookies', async () => {
        console.log('reset_alb_cookies', new Date());
        resetAlbCookies();
        disconnect(true);
        connect();
      });

      socketLocal.on('connect_error', (data, e) => {
        console.error(`[ServerRecognitionContext] Socket connect error:`, data);

        dispatch({
          type: ACTION.SET_CONNECTED,
          payload: { isConnected: false },
        });
        dispatch({ type: ACTION.SET_ERROR, payload: { error: data } });

        socketLocal.deferredPromise.reject(data);
      });

      socketLocal.on('reset_alb_cookies', async () => {
        console.log('reset_alb_cookies', new Date());
        resetAlbCookies();
        disconnect(true);
        connect();
      });

      const handleError = (data) => {
        const error = new Error(data?.message);
        error.code = data?.code;
        error.message_dev = data?.message_dev;
        if (error.code === RECOGNITION_ERROR_CODES.CONSENTS_REQUIRED) {
          error.message = 'Please agree to the new privacy policy';
        }
        dispatch({ type: ACTION.SET_ERROR, payload: { error: error } });

        logErrorDebounced(
          `[ServerRecognitionContext] Error code: ${error.code} msg: ${error.message}`,
        );
      };

      socketLocal.io.on('packet', (packet) => {
        if (!packet || !packet.data || !Array.isArray(packet.data)) {
          return;
        }

        const [event, data] = packet.data;
        if (event === 'error' && data) {
          handleError(data);
        }
      });

      socketLocal.on('error', (data) => {
        handleError(data);
      });
      socketLocal.on('connection_ready', () => {
        console.log('connection_ready', new Date());
        clearReadyTimer();
        dispatch({ type: ACTION.CONNECTION_READY });
      });
      socketLocal.on('request_received', (data) => {
        processingHandlerRef.current?.audioProcessing(data.request_id);
      });

      socketLocal.on('partial_recognition', (data) => {
        dispatch({ type: ACTION.SET_PARTIAL_RECOGNITION, payload: data });
      });
      socketLocal.on('recognition', onRecognition);
      socketLocal.on('wuw_verification', (data) => {
        processingHandlerRef.current?.audioProcessed(data.request_id);
      });
      socketLocal.on('model_missing', () => {
        console.log('model_missing', new Date());
        dispatch({
          type: ACTION.SET_MODEL_LOAD_STATUS,
          payload: { status: 'model_missing' },
        });
      });
      socketLocal.on('model_loading', () => {
        console.log('model_loading', new Date());
        dispatch({
          type: ACTION.SET_MODEL_LOAD_STATUS,
          payload: { status: 'model_loading' },
        });
      });
      socketLocal.on('model_loading_error', () => {
        console.log('model_loading_error', new Date());
        dispatch({
          type: ACTION.SET_MODEL_LOAD_STATUS,
          payload: { status: 'model_loading_error' },
        });
      });
      socketLocal.on('model_ready', (data) => {
        console.log('model_ready', new Date());
        dispatch({ type: ACTION.SET_MODEL_INFO, payload: { modelInfo: data } });
        dispatch({
          type: ACTION.SET_MODEL_LOAD_STATUS,
          payload: { status: 'model_ready' },
        });
      });
      await socketLocal.deferredPromise.promise;
    } else if (!socketLocal.connected && !socketLocal.active) {
      //attempt to connect when socket.io manages reconnection causes error and disconnected state. `active` helps to understand if we really need to reconnect.
      console.log('[Connect] Connect');
      socketLocal.deferredPromise = createDeferred();

      // Undo `stopReconnecting`: this is a deliberate attempt, so the manager
      // gets to retry it again.
      socketLocal.io.reconnection(true);
      socketLocal.connect();
      await socketLocal.deferredPromise.promise;
    }

    return true;
  };

  const disconnect = (force = false) => {
    if (!force && (!socketRef.current || !state.isConnected)) {
      return false;
    }

    console.log('[Disconnect]');
    clearReadyTimer();
    isStreamProcessingRef.current = false;
    socketRef.current?.disconnect();

    processingHandlerRef.current?.removeEventListener(
      'change',
      onProcessingHandlerStateChange,
    );

    return true;
  };

  // Capping our own reconnect attempts is not enough to stop knocking on a
  // service that is down: socket.io's manager retries on its own schedule, so a
  // client that has "given up" still opens a connection every ~25s, forever.
  // Turning the manager's reconnection off is what actually ends it; `connect`
  // turns it back on, so an explicit attempt by the user still works.
  const stopReconnecting = () => {
    socketRef.current?.io?.reconnection(false);
    return disconnect(true);
  };

  const selectProvider = (
    provider,
    metadata,
    use_streaming_model = false,
    domain_id = 'beta_phase',
    scenario_id = 'beta_phase',
    language = 'en',
  ) => {
    if (!socketRef.current || !state.isConnected) {
      return false;
    }

    if (isStreamProcessingRef.current) {
      stopStreamProcessing();
      disconnect(true);
      connect();
    }

    dispatch({
      type: ACTION.SET_PROVIDER,
      payload: { provider },
    });

    socketRef.current.emit('select_provider', {
      provider_id: provider,
      domain_id,
      scenario_id,
      language,
      metadata,
      app_build: APP_BUILD,
      use_streaming_model,
    });
    return true;
  };

  const recognize = (audio, type = RECOGNITION_SAMPLE_TYPE.int16, duration) => {
    if (
      !socketRef.current ||
      !state.isConnected ||
      state.modelLoadStatus !== 'model_ready'
    ) {
      return false;
    }
    if (!audio.length) {
      return false;
    }
    if (processingHandlerRef.current.isProcessing) {
      return false;
    }

    // Unused today, but exported in `actions` -- a future caller must not be
    // able to ship audio under a save_audio value that was only correct back
    // when the socket opened.
    assertSaveAudio();

    socketRef.current.emit('recognize_audio_samples', audio, type);
    console.log(`sending audio length ${audio.length} type ${type}`);

    processingHandlerRef.current?.audioSent(duration);

    return true;
  };

  const canStream =
    socketRef.current &&
    socketRef.current.connected &&
    state.isReady &&
    state.modelLoadStatus === 'model_ready';

  const stream = (audio, type = RECOGNITION_SAMPLE_TYPE.float32) => {
    if (!canStream) {
      return false;
    }
    if (!audio.length) {
      return false;
    }
    // canStream above is a render-time snapshot that can lag a live
    // disconnect by at least a tick. Emitting anyway would not fail loudly --
    // socket.io queues it in its own sendBuffer and flushes it on reconnect,
    // ahead of the set_options the open handler sends, so the server would
    // apply stale audio under whatever save_audio setting was in effect
    // before the drop. This audio is useless to the recognizer mid-outage
    // regardless, so the live socket is checked again right before emitting.
    if (!socketRef.current?.connected) {
      return false;
    }

    socketRef.current.emit('stream_audio_samples', audio, type);

    return true;
  };

  const streamCompressedAudio = async (blob) => {
    if (!canStream) {
      return false;
    }
    if (!blob.size) {
      return false;
    }

    queueRef.current.push(async () => {
      const arrayBuffer = await blob.arrayBuffer();
      if (!isStreamProcessingRef.current) {
        return false;
      }
      // Same reasoning as stream() above: this task can run after the socket
      // it was queued under has gone down, and must not let socket.io buffer
      // the emit across the outage.
      if (!socketRef.current?.connected) {
        return false;
      }
      socketRef.current.emit('stream_compressed_audio', arrayBuffer, blob.type);
    });

    return true;
  };

  const setStreamingVadMinSilence = (duration = 0.3) => {
    if (!state || !state.isConnected) {
      return false;
    }

    return setOptions({ streaming_vad_min_silence_length: duration });
  };

  // Guarded on the live socket rather than on `state.isConnected`: the reducer
  // state is a render-time snapshot, and with the connection opened on demand
  // these run in the same tick as the dispatch that marks the socket connected,
  // before React has re-rendered. Reading the snapshot there would refuse to
  // start and the recording would stream into nothing.
  const startStreamProcessing = () => {
    if (!socketRef.current?.connected) {
      return false;
    }

    // A socket opened an hour ago may have been told the opposite of what is
    // true now, and a backgrounded tab's throttled timer may not have noticed
    // the window closing.
    //
    // Called directly, not queued: `stream()` emits audio straight onto the
    // socket without going through `queueRef`, so a caller that starts a
    // stream and immediately streams audio would be able to overtake a
    // queued assert here. Calling it synchronously guarantees it is on the
    // wire before this function returns, and so before any audio can be.
    assertSaveAudio();

    isStreamProcessingRef.current = true;
    return true;
  };

  const stopStreamProcessing = () => {
    if (!socketRef.current?.connected) {
      return false;
    }

    queueRef.current.push(async () => {
      isStreamProcessingRef.current = false;
      socketRef.current?.emit('stop_stream_processing');
      // Backstop, not the primary path: a value that falls due mid-stream is
      // now asserted immediately (there is no in-stream guard any more), so
      // this is normally a no-op via `lastSaveAudioRef`. It stays because the
      // primary path is a timer that a throttled background tab can delay,
      // and this one is nearly free.
      assertSaveAudio();
    });
    return true;
  };

  const setOptions = (options) => {
    if (!state || !state.isConnected) {
      return false;
    }

    socketRef.current?.emit('set_options', options);

    return true;
  };

  /**
   * Tells the server whether to keep this user's audio.
   *
   * The recognition server can take this change during active recognition,
   * so there is no in-stream guard any more: this is called from a settings
   * change, an expiry timer, a socket opening, and a recording starting or
   * stopping alike, and it asserts whenever the computed value differs from
   * what this socket was last told. Called with no live socket, it does
   * nothing -- it never opens a connection of its own; the next thing that
   * does open one (the socket-open handler) carries the value in as its
   * first message.
   *
   * `deferTrue` is for the immediate-assert path only (a settings change and
   * the expiry timer -- see the effect above): unlike every other call site,
   * that one can fire while a chunk captured before the window closed is
   * still sitting in `queueRef` from `streamCompressedAudio`. A direct emit
   * there would let `save_audio: true` overtake that chunk on the wire, so
   * the `true` direction is queued behind it instead. `save_audio: false`
   * only ever over-protects a queued chunk, so it always goes out directly
   * regardless of `deferTrue`; the other call sites never pass `deferTrue`
   * and keep their original direct-emit behaviour in both directions.
   */
  const assertSaveAudio = (deferTrue = false) => {
    const { isAllowed, until } = noAudioSaveRef.current;
    if (!isAllowed) {
      return;
    }
    if (!socketRef.current?.connected) {
      return;
    }

    const saveAudio = !(!!until && until > Date.now());
    if (saveAudio === lastSaveAudioRef.current) {
      return;
    }
    // Recorded as sent immediately, even for the queued branch below: this
    // is what makes every other call site's dedupe check refuse to send the
    // same `true` directly ahead of the queue while it is pending.
    lastSaveAudioRef.current = saveAudio;

    if (!saveAudio || !deferTrue) {
      pendingSaveAudioTrueRef.current = null;
      socketRef.current.emit('set_options', { save_audio: saveAudio });
      return;
    }

    const token = {};
    pendingSaveAudioTrueRef.current = token;
    queueRef.current.push(async () => {
      // A later change may have superseded this send (e.g. the window
      // re-opened, or some other call site already asserted a value) before
      // the queue drained down to this task. Only send if this is still
      // that pending request -- otherwise `lastSaveAudioRef` no longer
      // reflects `true` and sending it now would put a stale value on the
      // wire.
      if (pendingSaveAudioTrueRef.current !== token) {
        return;
      }
      pendingSaveAudioTrueRef.current = null;
      if (!socketRef.current?.connected) {
        return;
      }
      socketRef.current.emit('set_options', { save_audio: true });
    });
  };

  const setRecognitionMode = (mode) => {
    return setOptions({ recognition_mode: mode });
  };

  const setFilterProfanities = (value) => {
    return setOptions({ filter_profanities: value });
  };

  const refreshToken = (token) => {
    if (!state || !state.isConnected) {
      return false;
    }

    socketRef.current?.emit('refresh_token', { token });
  };

  const resetAlbCookies = async () => {
    try {
      await clientApi.resetAlbCookies();
    } catch (error) {
      console.error(error);
    }
  };

  const resetRecognition = () => dispatch({ type: ACTION.RESET_RECOGNITION });

  const clearSocket = () => {
    socketRef.current = null;
  };

  const actions = {
    connect,
    disconnect,
    selectProvider,
    recognize,
    setRecognitionMode,
    resetAlbCookies,
    resetRecognition,
    stream,
    streamCompressedAudio,
    setStreamingVadMinSilence,
    setFilterProfanities,
    startStreamProcessing,
    stopStreamProcessing,
    stopReconnecting,
    clearSocket,
  };

  return {
    ...state,
    canStream,
    actions,
  };
};

export const ServerRecognitionContext = createContext({ state: initialState });

export const ServerRecognitionContextProvider = ({ children }) => {
  const ssr = useServerRecognition();

  const { isAuthenticated, user, fetchUserTasks, socketAuthFailed } = useAuth();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const settings = useSettings();
  const prevIsAuthenticated = usePrevious(isAuthenticated);
  const modelsStorage = useReduxStorage('models');

  const casrInfo = useCasrInfo();
  const isAppInactive = useAppInactivity();
  const { isSubscriptionLoaded, isSubscriptionLocked } = useSubscription();

  const connectDeferredPromise = useRef();

  // Screens that can record claim the socket while they are mounted. Nothing
  // else opens it, so a user sitting on the notes list or in settings costs the
  // recognition server nothing.
  const holdCountRef = useRef(0);
  const [hasHold, setHasHold] = useState(false);
  const prevHasHold = usePrevious(hasHold);

  const acquireHold = useCallback(() => {
    holdCountRef.current += 1;
    setHasHold(true);
  }, []);

  const releaseHold = useCallback(() => {
    holdCountRef.current = Math.max(0, holdCountRef.current - 1);
    setHasHold(holdCountRef.current > 0);
  }, []);

  const disconnectTimerRef = useRef(null);
  const isStreamingRef = useRef(false);

  const cancelScheduledDisconnect = () => {
    clearTimeout(disconnectTimerRef.current);
    disconnectTimerRef.current = null;
  };

  const scheduleDisconnect = (delay, reason) => {
    cancelScheduledDisconnect();
    disconnectTimerRef.current = setTimeout(() => {
      disconnectTimerRef.current = null;
      if (isStreamingRef.current) {
        return;
      }
      console.log(`[Disconnect] scheduled: ${reason}`);
      ssr.actions.disconnect(true);
    }, delay);
  };

  // Sticky, and it has to be: `selectProvider` clears the error, and without
  // this any provider re-selection would take the `!ssr.error` path below and
  // quietly restart a loop we just gave up on. Cleared only by an explicit new
  // intent -- opening a recording screen, pressing record, or a connection that
  // actually came up.
  const hasGivenUpRef = useRef(false);

  const connectMutex = async (fn) => {
    if (connectDeferredPromise.current) {
      await connectDeferredPromise.current.promise;
      return;
    }
    const deferredPromise = createDeferred();
    connectDeferredPromise.current = deferredPromise;
    try {
      await fn();
    } catch (error) {
      logger.error(
        `[ServerRecognitionContext] connect failed: ${error.message}`,
      );
    } finally {
      deferredPromise.resolve();
      connectDeferredPromise.current = null;
    }
  };

  // Retries are queued behind `connectMutex` and each waits out its own backoff,
  // so by the time the last one runs the client may already have given up --
  // measured: a retry queued at 144s opened a connection at 191s, 27s after
  // "Giving up". Checking the flag here, after the wait, is what makes giving up
  // mean it. `retryConnection` clears the flag before calling in, so a
  // deliberate attempt is never dropped.
  const connect = async (delay) => {
    await connectMutex(async () => {
      if (delay) {
        await wait(delay);
      }
      if (hasGivenUpRef.current) {
        return;
      }
      await ssr.actions.connect();
    });
  };

  const reConnect = async (delay) => {
    await connectMutex(async () => {
      if (delay) {
        await wait(delay);
      }
      if (hasGivenUpRef.current) {
        return;
      }
      await socketAuthFailed();
      await ssr.actions.connect();
    });
  };

  // Retries used to be a fixed delay with no cap, so every client rejected by a
  // busy server came back in lockstep and kept coming back forever. Exponential
  // backoff with full jitter spreads them out, and the cap makes a client give
  // up and surface the error instead of adding load indefinitely.
  const reconnectAttemptsRef = useRef(0);
  // Giving up is otherwise silent: the socket is gone, nothing retries, and the
  // record button looks perfectly normal until it is pressed. Say so, and give
  // the user a way back without hunting for one.
  const giveUpSnackbarRef = useRef(null);

  const nextRetryDelay = () => {
    const attempt = reconnectAttemptsRef.current;
    reconnectAttemptsRef.current = attempt + 1;
    const backoff = Math.min(
      RECOGNITION_RECONNECT_BASE_DELAY_MS * 2 ** attempt,
      RECOGNITION_RECONNECT_MAX_DELAY_MS,
    );
    return Math.round(backoff * (0.5 + Math.random() * 0.5));
  };

  useEffect(() => {
    if (ssr.isConnected) {
      reconnectAttemptsRef.current = 0;
      hasGivenUpRef.current = false;
      if (giveUpSnackbarRef.current) {
        closeSnackbar(giveUpSnackbarRef.current);
        giveUpSnackbarRef.current = null;
      }
    }
  }, [ssr.isConnected]);

  // Reconnect on the user's say-so. The attempt budget and the give-up flag are
  // both cleared: this is a fresh intent, not a continuation of the failed run.
  const retryConnection = () => {
    reconnectAttemptsRef.current = 0;
    hasGivenUpRef.current = false;
    connect();
  };

  const showGiveUpMessage = () => {
    if (giveUpSnackbarRef.current) {
      return;
    }
    giveUpSnackbarRef.current = enqueueSnackbar(
      'Could not reach the recognition service',
      {
        variant: 'error',
        persist: true,
        action: (key) => (
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              closeSnackbar(key);
              giveUpSnackbarRef.current = null;
              retryConnection();
            }}
          >
            Retry
          </Button>
        ),
      },
    );
  };

  const canConnect =
    isAuthenticated &&
    user &&
    isSubscriptionLoaded &&
    !isSubscriptionLocked &&
    !isAppInactive &&
    hasHold;

  useEffect(() => {
    const f = async () => {
      if (!canConnect) {
        return;
      }

      // A screen that can record has just been opened. Connect straight away so
      // the model is loaded before the user reaches for the record button, and
      // do not let a stale error from an earlier session delay it.
      if (!prevHasHold) {
        reconnectAttemptsRef.current = 0;
        hasGivenUpRef.current = false;
        cancelScheduledDisconnect();
        connect();
        return;
      }

      const isMissingConsentError =
        ssr.error?.code === RECOGNITION_ERROR_CODES.CONSENTS_REQUIRED;
      const isFailedToLoginError =
        ssr.error?.code === RECOGNITION_ERROR_CODES.FAILED_TO_LOGIN;
      const isAuthError =
        'authentication failed' === ssr.error?.message || isFailedToLoginError;
      const isCapacityError = 'Capacity exceeded' === ssr.error?.message;

      if (isMissingConsentError) {
        return; // do not reconnect
      }

      // The session is held by another client. The socket itself is fine, so
      // reconnecting cannot clear this -- it would only spend the attempt
      // budget that a real connection failure needs later. The record button
      // re-asserts the provider when the user next presses it.
      const isSessionHeldElsewhere =
        ssr.error?.code === RECOGNITION_ERROR_CODES.SESSION_HELD_ELSEWHERE;
      if (isSessionHeldElsewhere && ssr.isConnected) {
        return;
      }

      if (hasGivenUpRef.current) {
        return;
      }

      if (!ssr.error) {
        connect();
        return;
      }

      // Every other error retries through the same backoff, including ones we
      // do not recognise -- an unclassified error used to reconnect with no
      // delay at all, which turns a repeating server-side failure into a tight
      // loop against an already struggling service.
      if (reconnectAttemptsRef.current >= RECOGNITION_MAX_RECONNECT_ATTEMPTS) {
        logger.error(
          `[ServerRecognitionContext] Giving up after ${reconnectAttemptsRef.current} reconnect attempts: ${ssr.error?.message}`,
        );
        hasGivenUpRef.current = true;
        // Not just "stop calling connect": socket.io keeps its own retry loop
        // running, and leaving it on means every open tab goes on hitting a
        // failing service indefinitely -- the load this cap exists to remove.
        ssr.actions.stopReconnecting();
        showGiveUpMessage();
        return;
      }

      const delay = nextRetryDelay();
      if (isAuthError) {
        reConnect(delay);
        return;
      }
      if (isCapacityError) {
        ssr.actions.resetAlbCookies();
      }
      connect(delay);
    };
    f();
  }, [
    canConnect,
    // Added ssr.isConnected for case when another streaming session is running
    // and when we try to start streaming, socket is disconnected and doesn't
    // try to reconnect because server aborted connection with code 41
    // ssr.isConnected,
    ssr.error,
  ]);

  // Nobody needs the socket any more. Held back by a grace period so navigating
  // between recognition screens -- old one unmounts before the new one mounts --
  // does not tear the connection down and immediately rebuild it.
  useEffect(() => {
    if (hasHold) {
      cancelScheduledDisconnect();
      return;
    }
    if (!prevHasHold) {
      return;
    }
    // Nothing is on screen to record any more, so a recording flag left behind
    // by an unmount cannot block the scheduled disconnect.
    isStreamingRef.current = false;
    scheduleDisconnect(
      RECOGNITION_RELEASE_GRACE_MS,
      'no screen holds the socket',
    );
  }, [hasHold]);

  // Held but unused: the user is sitting on a recognition screen without
  // recording. Drop the connection and reopen it on demand when they press
  // record. Without this a visible tab keeps its socket -- and its model slot --
  // for as long as it stays open.
  useEffect(() => {
    if (!ssr.canStream || isStreamingRef.current) {
      return;
    }
    scheduleDisconnect(RECOGNITION_IDLE_DISCONNECT_MS, 'idle on screen');
  }, [ssr.canStream]);

  const canStreamDeferredRef = useRef(null);
  // Mirrors `canStream` outside the render snapshot. `connectAndWaitForReady`
  // runs from an event handler built in an earlier render, so reading the
  // reducer state there can say "not ready" about a connection that is already
  // up -- and then the effect below has nothing left to fire on and the waiter
  // hangs until RECOGNITION_CONNECT_TIMEOUT_MS. Pressing record just as the
  // model finished loading is exactly that window.
  const canStreamRef = useRef(false);

  useEffect(() => {
    canStreamRef.current = ssr.canStream;
    if (ssr.canStream && canStreamDeferredRef.current) {
      canStreamDeferredRef.current.resolve();
    }
  }, [ssr.canStream]);

  // Same reason as `canStreamRef`: read from an event handler, where the
  // reducer state captured by that render can be stale.
  const errorRef = useRef(null);
  useEffect(() => {
    errorRef.current = ssr.error;
    // A waiter started before the refusal landed would otherwise sit through
    // the whole connection timeout for a connection that is never going to be
    // allowed to stream. End it with the server's own wording.
    if (
      ssr.error?.code === RECOGNITION_ERROR_CODES.SESSION_HELD_ELSEWHERE &&
      canStreamDeferredRef.current
    ) {
      const error = new Error(ssr.error.message);
      error.isUserNotified = true;
      canStreamDeferredRef.current.reject(error);
    }
  }, [ssr.error]);

  // Re-sends `select_provider` on the socket that is already open. Held in a
  // ref because the effect that owns it is declared further down, while the
  // caller below runs from an event handler.
  const reselectProviderRef = useRef(null);

  // Opens the connection and resolves once it can actually stream, so pressing
  // record after an idle disconnect still works. Concurrent callers share one
  // deferred, so the socket is only built once.
  const connectAndWaitForReady = async () => {
    cancelScheduledDisconnect();

    if (canStreamRef.current) {
      return;
    }

    // One live recognition session per user: a second client gets its
    // `select_provider` refused, so its socket is up but it can never stream.
    // Waiting here would only end in the connection timeout -- a full minute of
    // spinner that looks like progress and then fails anyway. Say what the
    // server said instead, and re-assert the provider so the next press works
    // as soon as the other session lets go.
    if (
      errorRef.current?.code === RECOGNITION_ERROR_CODES.SESSION_HELD_ELSEWHERE
    ) {
      reselectProviderRef.current?.();
      const error = new Error(errorRef.current.message);
      error.isUserNotified = true;
      throw error;
    }

    // Pressing record is a fresh intent: give it the full budget again, so a
    // client that gave up earlier is not stuck refusing to try.
    reconnectAttemptsRef.current = 0;
    hasGivenUpRef.current = false;

    if (!canStreamDeferredRef.current) {
      canStreamDeferredRef.current = createDeferred();
    }
    const deferred = canStreamDeferredRef.current;

    // Not awaited: the effect above resolves the deferred once `canStream`
    // turns true, which happens after several more round trips.
    connect();

    // ...unless it turned true while this call was being made, in which case
    // that effect has already run and would never run again for this deferred.
    if (canStreamRef.current) {
      deferred.resolve();
    }

    const timeoutId = setTimeout(
      () => deferred.reject(new Error('Recognition connection timeout')),
      RECOGNITION_CONNECT_TIMEOUT_MS,
    );

    try {
      await deferred.promise;
    } finally {
      clearTimeout(timeoutId);
      if (canStreamDeferredRef.current === deferred) {
        canStreamDeferredRef.current = null;
      }
    }
  };

  // Wrapped so the provider knows whether audio is flowing: the idle timer must
  // never fire mid-recording, and it has to be re-armed once recording ends.
  const startStreamProcessing = () => {
    cancelScheduledDisconnect();
    isStreamingRef.current = true;
    return ssr.actions.startStreamProcessing();
  };

  const stopStreamProcessing = () => {
    const wasStreaming = isStreamingRef.current;
    isStreamingRef.current = false;
    const result = ssr.actions.stopStreamProcessing();
    // Only a recording that actually ran re-arms the idle timer. Leaving a
    // recognition screen lands here too -- the recorder stops itself on unmount
    // and that fires `onRecordEnd` even if nothing was ever recorded -- and
    // since `scheduleDisconnect` replaces whatever timer is pending, an
    // unconditional re-arm here would swap the 10s release grace for 5 minutes
    // and keep a model slot long after the user left every screen that records.
    if (wasStreaming) {
      scheduleDisconnect(
        RECOGNITION_IDLE_DISCONNECT_MS,
        'idle after recording',
      );
    }
    return result;
  };

  const actions = {
    ...ssr.actions,
    startStreamProcessing,
    stopStreamProcessing,
  };

  const useStreamingModel = useMemo(
    () => user?.settings.app?.use_streaming_model,
    [user?.settings.app?.use_streaming_model],
  );

  const isDiscreteRecogitionOn =
    casrInfo.isPersonalAvailable &&
    settings.state[SettingsKeys.personalRecognitionOn] &&
    _.get(user?.settings, SETTINGS_DISCRETE_ENABLED_PATH);

  const isIntermediateRecognitionOn =
    settings.state[SettingsKeys.isIntermediateRecognitionOn];
  useEffect(() => {
    const updateProvider = async () => {
      if (!ssr.isConnected || !casrInfo.provider) {
        return;
      }
      const metadata = await getDeviceInfo();
      ssr.actions.selectProvider(
        casrInfo?.provider,
        JSON.stringify(metadata),
        !isDiscreteRecogitionOn &&
          useStreamingModel &&
          isIntermediateRecognitionOn,
        isDiscreteRecogitionOn ? 'user_phrasebook' : 'beta_phase',
      );
    };
    // Kept reachable for the record button, which re-asserts the provider after
    // the server refused the session to another client.
    reselectProviderRef.current = () =>
      updateProvider().catch((err) =>
        logger.error(
          `[ServerRecognitionContext] provider re-select error ${err.message}`,
        ),
      );
    updateProvider().catch((err) =>
      logger.error(
        `[ServerRecognitionContext] updateProvider error ${err.message}`,
      ),
    );
  }, [
    casrInfo?.provider,
    ssr.isConnected,
    useStreamingModel,
    isDiscreteRecogitionOn,
    isIntermediateRecognitionOn,
  ]);

  //Disconnect on timeout
  useEffect(() => {
    if (isAppInactive) {
      cancelScheduledDisconnect();
      isStreamingRef.current = false;
      ssr.actions.disconnect(true);
    }
  }, [isAppInactive]);

  //Disconnect on logout
  useEffect(() => {
    const isLoggedOut = !isAuthenticated && prevIsAuthenticated;
    if (isLoggedOut) {
      cancelScheduledDisconnect();
      isStreamingRef.current = false;
      ssr.actions.resetAlbCookies();
      ssr.actions.disconnect(true);
      ssr.actions.clearSocket();
    }
  }, [isAuthenticated, prevIsAuthenticated]);

  useEffect(() => cancelScheduledDisconnect, []);

  //Update models and tasks for 'Recognition ready' screen and having latest models
  useEffect(() => {
    if (ssr.modelLoadStatus === 'model_ready') {
      fetchUserTasks();
      modelsStorage.loadForced();
    }
  }, [ssr.modelLoadStatus]);

  return (
    <ServerRecognitionContext.Provider
      value={{
        ...ssr,
        actions,
        casrInfo,
        isInitializing:
          casrInfo.isModelBuilt && !ssr.canStream && ssr.isConnecting,
        reConnect,
        connectAndWaitForReady,
        acquireHold,
        releaseHold,
      }}
    >
      {children}
    </ServerRecognitionContext.Provider>
  );
};
